/**
 * How tool names are shown everywhere a customer sees them (prototype TOOL_LABEL / TOOL_DENYLIST / byMatch).
 * - One label per tool: "Google chat" and "Google Chat" collapse; the variant with the most capitals wins,
 *   and any all-lowercase word is capitalised.
 * - Generic communication and productivity tools are not professional software: hidden on cards, the Tools
 *   filter and sign-up (the profile's Software skills shows every tool, product 2026-10-24). A denylist, so new
 *   industry software shows up without a change.
 */
export const TOOL_DENYLIST: ReadonlySet<string> = new Set([
  "whatsapp", "whatsapp business", "google workspace", "google chat", "google meet", "google voice",
  "zoom", "slack", "microsoft teams", "teams", "microsoft outlook + teams", "skype", "gmail", "outlook", "microsoft outlook", "messenger", "discord",
  "ringcentral", "vonage business", "genesys", "microsoft paint", "canva", "capcut", "adobe photoshop",
  "adobe premiere pro", "adobe illustrator",
]);

/** Same tool whatever the case or surrounding spaces (the marketplace filter matches case-insensitively too). */
export const toolKey = (name: string) => name.trim().toLowerCase();

export const isProTool = (name: string) => !TOOL_DENYLIST.has(toolKey(name));

const capitals = (s: string) => (s.match(/[A-Z]/g) || []).length;
const capitaliseLowerWords = (s: string) => s.split(" ").map((w) => (/^[a-z]+$/.test(w) ? w[0].toUpperCase() + w.slice(1) : w)).join(" ");

/** The display label for every spelling of each tool, keyed by toolKey. */
export function toolLabels(names: string[]): Map<string, string> {
  const groups = new Map<string, string[]>();
  names.forEach((n) => {
    const t = n.trim();
    if (!t) return;
    const k = toolKey(t);
    groups.set(k, [...(groups.get(k) || []), t]);
  });
  const out = new Map<string, string>();
  groups.forEach((variants, k) => {
    const best = [...variants].sort((a, b) => capitals(b) - capitals(a))[0];
    out.set(k, capitaliseLowerWords(best));
  });
  return out;
}

/** One label per tool, in first-seen order. Generic tools included (the profile shows every tool). */
export function uniqueTools(names: string[]): string[] {
  const labels = toolLabels(names);
  const seen = new Set<string>();
  return names.reduce<string[]>((acc, n) => {
    const k = toolKey(n);
    if (!k || seen.has(k)) return acc;
    seen.add(k);
    acc.push(labels.get(k) as string);
    return acc;
  }, []);
}

/** One label per tool, professional software only, in first-seen order. */
export const cleanTools = (names: string[]): string[] => uniqueTools(names).filter(isProTool);

/** The customer's own tools first, then the rest (prototype byMatch). */
export function byMatch(tools: string[], mine: string[]): string[] {
  const own = new Set(mine.map(toolKey));
  return [...tools].sort((a, b) => Number(own.has(toolKey(b))) - Number(own.has(toolKey(a))));
}

export const isMyTool = (tool: string, mine: string[]) => mine.some((m) => toolKey(m) === toolKey(tool));

/** Each tool's logo (marketplace tools.logoUrl) by toolKey, so any spelling of a tool finds it. https only. */
export function toolLogos(tools: ReadonlyArray<{ name: string; logoUrl?: string }>): Map<string, string> {
  const out = new Map<string, string>();
  tools.forEach((t) => {
    const k = toolKey(t.name || "");
    if (k && !out.has(k) && typeof t.logoUrl === "string" && t.logoUrl.startsWith("https://")) out.set(k, t.logoUrl);
  });
  return out;
}
